import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import styled from "styled-components";
import { useParams, useSearchParams } from "react-router-dom";
import { httpsCallable, getFunctions } from "firebase/functions";
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { Oval } from "react-loader-spinner";
import { app } from "../config/firebaseConfig";
import micIcon from '../assets/mic_icon.png';
import sendIcon from "../assets/send_icon.png";

export default function Aprofundamento() {
  useParams();
  const [searchParams] = useSearchParams();
  const accessToken = (searchParams.get('t') || '').toString().trim();

  const [loadState, setLoadState] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [nome, setNome] = useState('');
  const [status, setStatus] = useState('aberto');
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [isListening, setIsListening] = useState(false);

  const messageEndRef = useRef(null);
  const lastSpokenIndexRef = useRef(-1);
  const functions = useMemo(() => getFunctions(app, "southamerica-east1"), []);
  const { transcript, resetTranscript, listening, browserSupportsSpeechRecognition } = useSpeechRecognition();

  useEffect(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollTop = messageEndRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (listening) {
      setUserInput(transcript);
    }
  }, [transcript, listening]);

  const speak = useCallback((text) => {
    if (!audioEnabled || !text || !('speechSynthesis' in window)) return;
    try {
      window.speechSynthesis.cancel();
      const utterance = new window.SpeechSynthesisUtterance(text);
      utterance.lang = 'pt-BR';
      window.speechSynthesis.speak(utterance);
    } catch (err) {
      console.warn('Falha ao reproduzir áudio da resposta:', err);
    }
  }, [audioEnabled]);

  useEffect(() => {
    if (!messages.length) return;
    const lastIndex = messages.length - 1;
    const last = messages[lastIndex];
    if (last.role === 'agent' && lastIndex > lastSpokenIndexRef.current) {
      lastSpokenIndexRef.current = lastIndex;
      speak(last.text);
    }
  }, [messages, speak]);

  useEffect(() => {
    let cancelled = false;
    const loadSession = async () => {
      if (!accessToken) {
        setLoadState('invalid-link');
        return;
      }
      try {
        const getSession = httpsCallable(functions, "getAprofundamentoSessionByToken");
        const result = await getSession({ accessToken });
        if (cancelled) return;
        const data = result?.data || {};
        setNome(data.nome || '');
        setStatus(data.status || 'aberto');
        setMessages(Array.isArray(data.messages) ? data.messages : []);
        lastSpokenIndexRef.current = (data.messages || []).length - 1;
        setLoadState('ready');
      } catch (err) {
        console.error('Erro ao carregar sessão de aprofundamento:', err);
        if (cancelled) return;
        setErrorMessage(err?.message || 'Não foi possível carregar esta conversa.');
        setLoadState('error');
      }
    };
    loadSession();
    return () => { cancelled = true; };
  }, [accessToken, functions]);

  const toggleMic = () => {
    if (isListening) {
      SpeechRecognition.stopListening();
      setIsListening(false);
    } else {
      resetTranscript();
      SpeechRecognition.startListening({ continuous: true, language: 'pt-BR' });
      setIsListening(true);
    }
  };

  const handleSend = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const message = userInput.trim();
    if (!message || isSending || status !== 'aberto') return;

    if (isListening) {
      SpeechRecognition.stopListening();
      setIsListening(false);
      resetTranscript();
    }

    setMessages((prev) => [...prev, { role: 'user', text: message, at: new Date().toISOString() }]);
    setUserInput('');
    setIsSending(true);
    try {
      const sendMessage = httpsCallable(functions, "sendAprofundamentoMessage");
      const result = await sendMessage({ accessToken, message });
      const data = result?.data || {};
      setMessages((prev) => [...prev, { role: 'agent', text: data.reply || '', at: new Date().toISOString() }]);
      if (data.status) setStatus(data.status);
    } catch (err) {
      console.error('Erro ao enviar mensagem de aprofundamento:', err);
      setMessages((prev) => [...prev, { role: 'agent', text: 'Desculpe, houve um erro ao processar sua resposta. Tente novamente.', at: new Date().toISOString() }]);
    } finally {
      setIsSending(false);
    }
  };

  if (loadState === 'loading') {
    return (
      <Wrapper>
        <Oval height={48} width={48} color="#000" secondaryColor="#ccc" strokeWidth={4} />
      </Wrapper>
    );
  }

  if (loadState === 'invalid-link' || loadState === 'error') {
    return (
      <Wrapper>
        <Container>
          <h1>Link inválido ou expirado</h1>
          <p>{errorMessage || 'Verifique se você copiou o link completo recebido por e-mail.'}</p>
        </Container>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <MainContainer>
        <Header>
          <div>
            <h1>Conversa de acompanhamento</h1>
            {nome && <p>Olá, {nome}.</p>}
            <PrivacyBadge>🔒 Conversa confidencial</PrivacyBadge>
          </div>
          <AudioToggle
            type="button"
            active={audioEnabled}
            onClick={() => setAudioEnabled((v) => !v)}
            title={audioEnabled ? 'Desativar leitura em voz alta' : 'Ativar leitura em voz alta'}
          >
            {audioEnabled ? '🔊 Áudio ativado' : '🔇 Ativar áudio'}
          </AudioToggle>
        </Header>

        <MessageContainer ref={messageEndRef}>
          {messages.map((msg, index) => (
            <MessageBox key={index} role={msg.role}>
              <Message role={msg.role}>{msg.text}</Message>
            </MessageBox>
          ))}
          {isSending && (
            <MessageBox role="agent">
              <Message role="agent">
                <TypingDots>
                  <span /><span /><span />
                </TypingDots>
              </Message>
            </MessageBox>
          )}
        </MessageContainer>

        {status === 'concluido' ? (
          <ConcludedBanner>
            Esta conversa foi concluída. Obrigado por participar — sua contribuição é confidencial e será
            considerada.
          </ConcludedBanner>
        ) : (
          <InteractiveContainer>
            <form onSubmit={handleSend}>
              <StyledInput
                type="text"
                placeholder="Digite sua resposta..."
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                disabled={isSending}
              />
              <SendButton type="submit" disabled={isSending || !userInput.trim()}>
                <img src={sendIcon} alt="enviar" />
              </SendButton>
            </form>
            {browserSupportsSpeechRecognition && (
              <MicButton type="button" onClick={toggleMic} isListening={isListening} title="Falar em vez de digitar">
                <img src={micIcon} alt="microfone" />
              </MicButton>
            )}
          </InteractiveContainer>
        )}
      </MainContainer>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f8f9fb;
  padding: 20px;
`;

const Container = styled.div`
  max-width: 480px;
  text-align: center;
  font-family: 'Roboto', sans-serif;
  color: #333;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 560px;
  height: 80vh;
  max-height: 720px;
  background-color: #fcfeff;
  box-shadow: 20px 20px 20px #baccde8a;
  border: 1px solid #d1d1d1;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  background-color: #111;
  color: #fff;

  h1 {
    font-family: 'Poppins', sans-serif;
    font-size: 16px;
    margin: 0;
  }

  p {
    font-family: 'Roboto', sans-serif;
    font-size: 12px;
    margin: 4px 0 0 0;
    color: #ccc;
  }
`;

const PrivacyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #9ee6b8;
`;

const TypingDots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: currentColor;
    opacity: 0.4;
    animation: typing-blink 1.2s infinite ease-in-out;
  }

  span:nth-child(2) {
    animation-delay: 0.2s;
  }

  span:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes typing-blink {
    0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
    40% { opacity: 1; transform: translateY(-2px); }
  }
`;

const AudioToggle = styled.button`
  flex-shrink: 0;
  background-color: ${(props) => (props.active ? '#28a745' : 'rgba(255,255,255,0.15)')};
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
`;

const MessageContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 20px;
  overflow-y: auto;
`;

const MessageBox = styled.div`
  display: flex;
  justify-content: ${(props) => (props.role === 'agent' ? 'flex-start' : 'flex-end')};
`;

const Message = styled.div`
  font-family: 'Roboto', sans-serif;
  font-size: 15px;
  line-height: 1.4;
  background-color: ${(props) => (props.role === 'agent' ? 'rgba(0,0,0,0.06)' : '#111')};
  color: ${(props) => (props.role === 'agent' ? '#111' : '#fff')};
  padding: 12px 14px;
  border-radius: 12px;
  max-width: 80%;
  white-space: pre-wrap;
`;

const InteractiveContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #eee;

  form {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const StyledInput = styled.input`
  flex: 1;
  height: 48px;
  border-radius: 12px;
  border: 1px solid #d1d1d1;
  padding: 0 14px;
  font-family: 'Roboto', sans-serif;
  font-size: 15px;

  &:focus {
    outline: 1px solid #111;
  }
`;

const SendButton = styled.button`
  height: 48px;
  width: 48px;
  border-radius: 12px;
  border: none;
  background-color: #111;
  cursor: pointer;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const MicButton = styled.button`
  height: 48px;
  width: 48px;
  border-radius: 12px;
  border: none;
  background-color: ${(props) => (props.isListening ? '#28a745' : '#e5e5e5')};
  cursor: pointer;
  flex-shrink: 0;

  img {
    width: 22px;
    height: 22px;
  }
`;

const ConcludedBanner = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #eee;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  color: #333;
  background-color: #f4faf5;
`;
